import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import MemoryLanePromo from "@/components/MemoryLanePromo";
import SiteDiscussion from "@/components/SiteDiscussion";
import { useSeo } from "@/lib/seo";
import {
  MapPin, Globe, CalendarBlank, GraduationCap, UsersThree, EnvelopeSimple, SealCheck, ShieldCheck, Sparkle, Translate,
  LinkSimple, FilmSlate, MusicNotes, NotePencil, ChatsCircle,
} from "@phosphor-icons/react";

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

const THEMES = {
  scholar: {
    font: "'Georgia', serif", bg: "#faf8f3", surface: "#ffffff", text: "#1a2b25",
    primary: "#1f3d33", accent: "#b8862b", heroText: "#ffffff",
  },
  campus: {
    font: "'Poppins', system-ui, sans-serif", bg: "#fffaf3", surface: "#ffffff", text: "#2b2018",
    primary: "#d97706", accent: "#0ea5e9", heroText: "#ffffff",
  },
  lab: {
    font: "'Inter', system-ui, sans-serif", bg: "#f5f9fa", surface: "#ffffff", text: "#0f2b2e",
    primary: "#0d9488", accent: "#155e75", heroText: "#ffffff",
  },
};

const VERIF = {
  official: { label: "Official", icon: ShieldCheck },
  verified: { label: "Verified", icon: SealCheck },
  claimed: { label: "Claimed", icon: SealCheck },
  unclaimed: { label: "Community", icon: UsersThree },
};

function Section({ id, title, t, children }) {
  return (
    <section id={id} className="mx-auto max-w-5xl px-6 py-14" data-testid={`site-section-${id}`}>
      {title && <h2 className="mb-6 text-2xl font-semibold" style={{ color: t.primary, fontFamily: t.font }}>{title}</h2>}
      {children}
    </section>
  );
}

export default function PublicSite() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [lang, setLang] = useState(() => {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang) return urlLang;
    try { return localStorage.getItem(`site_lang_${slug}`) || ""; } catch { return ""; }
  });
  const autoRef = useRef(false);

  useEffect(() => {
    api.get(`/site/${slug}${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [slug, lang]);

  // Auto-detect the visitor's browser language once — only if they haven't explicitly chosen one.
  useEffect(() => {
    if (autoRef.current || !d || d === false) return;
    autoRef.current = true;
    const explicit = new URLSearchParams(window.location.search).get("lang") || (() => { try { return localStorage.getItem(`site_lang_${slug}`); } catch { return null; } })();
    if (explicit) return;
    const avail = (d.available_languages || []).map((l) => l.code);
    if (avail.length <= 1) return;
    const navs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ""];
    for (const n of navs) {
      const base = (n || "").toLowerCase().split("-")[0];
      if (base && avail.includes(base)) { if (base !== d.lang) setLang(base); break; }
    }
  }, [d, slug]);

  const chooseLang = (code) => {
    try { localStorage.setItem(`site_lang_${slug}`, code); } catch { /* ignore */ }
    setLang(code);
  };

  useSeo(d && d !== false ? d.name : "Institution", d && d !== false ? (d.tagline || d.description) : null, d && d !== false ? d.logo_url : null);

  if (d === null) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;
  if (d === false) return <div className="grid min-h-screen place-items-center text-muted-foreground">This website isn't published yet.</div>;

  const t = THEMES[d.theme] || THEMES.scholar;
  const has = (m) => (d.modules || []).includes(m);
  const V = VERIF[d.verification] || VERIF.unclaimed;
  const navItems = (d.modules || []).filter((m) => m !== "hero").slice(0, 7);
  const T = d.section_titles || {};
  const langs = d.available_languages || [];

  return (
    <div dir={d.rtl ? "rtl" : "ltr"} style={{ background: t.bg, color: t.text, fontFamily: t.font, minHeight: "100vh" }} data-testid="public-site">
      {/* top bar */}
      <header className="sticky top-0 z-30 border-b backdrop-blur" style={{ background: `${t.surface}ee`, borderColor: "#0001" }}>
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2.5">
            {d.logo_url ? <img src={d.logo_url} alt={d.name} className="h-9 w-9 rounded-md object-cover" /> : <div className="grid h-9 w-9 place-items-center rounded-md text-white" style={{ background: t.primary }}><GraduationCap size={18} weight="fill" /></div>}
            <span className="font-semibold" style={{ fontFamily: t.font }}>{d.name}</span>
          </div>
          <div className="flex items-center gap-4">
            <nav className="hidden gap-5 text-sm md:flex">
              {navItems.map((m) => <a key={m} href={`#${m}`} className="capitalize opacity-70 transition-opacity hover:opacity-100">{T[m] || m.replace("_", " ")}</a>)}
            </nav>
            {langs.length > 1 && (
              <div className="flex items-center gap-1.5" data-testid="lang-switcher">
                <Translate size={16} style={{ color: t.accent }} />
                <select
                  value={d.lang}
                  onChange={(e) => chooseLang(e.target.value)}
                  className="cursor-pointer rounded-md border bg-transparent px-2 py-1 text-sm outline-none"
                  style={{ borderColor: "#0002", color: t.text }}
                  data-testid="lang-select"
                >
                  {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden px-6 py-24 text-center" style={{ background: t.primary, color: t.heroText }} data-testid="site-hero">
        <div className="mx-auto max-w-3xl">
          <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs"><V.icon size={13} weight="fill" /> {V.label}</span>
          <h1 className="text-4xl font-bold leading-tight sm:text-5xl" style={{ fontFamily: t.font }}>{d.sections?.hero?.headline || d.name}</h1>
          {(d.sections?.hero?.subtext || d.tagline) && <p className="mx-auto mt-4 max-w-xl text-lg opacity-90">{d.sections?.hero?.subtext || d.tagline}</p>}
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {d.website && <a href={d.website} target="_blank" rel="noreferrer"><button className="rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: t.accent, color: "#fff" }}>{d.sections?.cta || "Visit official site"}</button></a>}
            {has("alumni") && <a href="#alumni"><button className="rounded-full border border-white/40 px-5 py-2.5 text-sm font-medium">Alumni network</button></a>}
          </div>
        </div>
      </section>

      {/* quick facts */}
      <div className="relative z-10 mx-auto -mt-8 max-w-5xl px-6">
        <div className="grid grid-cols-2 gap-3 rounded-2xl border p-4 shadow-sm sm:grid-cols-4" style={{ background: t.surface, borderColor: "#0001" }}>
          {[
            { icon: MapPin, label: "Location", val: d.location || "—" },
            { icon: Globe, label: "Country", val: d.country || "—" },
            { icon: CalendarBlank, label: "Founded", val: d.founded || "—" },
            { icon: UsersThree, label: "Alumni", val: d.alumni_count ?? "—" },
          ].map((f) => (
            <div key={f.label} className="text-center">
              <f.icon size={18} className="mx-auto" style={{ color: t.accent }} />
              <div className="mt-1 font-semibold">{f.val}</div>
              <div className="text-xs opacity-60">{f.label}</div>
            </div>
          ))}
        </div>
      </div>

      {has("about") && (
        <Section id="about" title={T.about || "About"} t={t}>
          <p className="whitespace-pre-line text-base leading-relaxed opacity-90">{d.sections?.about?.body || d.description || "—"}</p>
          {(d.subtypes?.length > 0 || d.education_levels?.length > 0) && (
            <div className="mt-5 flex flex-wrap gap-2">
              {[...(d.subtypes || []), ...(d.education_levels || [])].slice(0, 10).map((x) => (
                <span key={x} className="rounded-full px-3 py-1 text-xs" style={{ background: `${t.accent}1a`, color: t.primary }}>{x}</span>
              ))}
            </div>
          )}
        </Section>
      )}

      {has("departments") && d.departments?.length > 0 && (
        <Section id="departments" title={T.departments || "Departments"} t={t}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {d.departments.map((dep) => (
              <div key={dep} className="rounded-xl border p-4" style={{ background: t.surface, borderColor: "#0001" }}><GraduationCap size={18} style={{ color: t.accent }} /><div className="mt-2 font-medium">{dep}</div></div>
            ))}
          </div>
        </Section>
      )}

      {(has("courses") || has("academics")) && d.courses?.length > 0 && (
        <Section id="courses" title={T.courses || "Programs & Courses"} t={t}>
          <div className="grid gap-3 sm:grid-cols-2">
            {d.courses.map((c) => (
              <Link key={c.id} to={`/courses/${c.id}`} className="rounded-xl border p-4 transition-shadow hover:shadow-md" style={{ background: t.surface, borderColor: "#0001" }}>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs opacity-60">{[c.degree_type, c.department, c.duration].filter(Boolean).join(" · ")}</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {has("faculty") && d.faculty?.length > 0 && (
        <Section id="faculty" title={T.faculty || "Faculty & Staff"} t={t}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {d.faculty.map((f, i) => (
              <div key={i} className="text-center">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-full text-white" style={{ background: t.primary }}>{(f.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
                <div className="mt-2 text-sm font-medium">{f.name}</div>
                <div className="text-xs opacity-60">{f.title}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {has("gallery") && d.photos?.length > 0 && (
        <Section id="gallery" title={T.gallery || "Campus"} t={t}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {d.photos.map((p, i) => <img key={i} src={p.startsWith("http") ? p : fileUrl(p)} alt="Campus" className="aspect-square w-full rounded-xl object-cover" />)}
          </div>
        </Section>
      )}

      {d.sections?.gallery?.length > 0 && (
        <Section id="photos" title="Photos" t={t}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="site-photos">
            {d.sections.gallery.map((g, i) => (
              <figure key={i}>
                <img src={(g.url || g.file_id || "").startsWith?.("http") ? g.url : fileUrl(g.file_id || g.url)} alt={g.caption || "Photo"} className="aspect-square w-full rounded-xl object-cover" />
                {g.caption && <figcaption className="mt-1 text-xs opacity-60">{g.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </Section>
      )}

      {d.sections?.media?.length > 0 && (
        <Section id="media" title="Videos & Audio" t={t}>
          <div className="grid gap-5 sm:grid-cols-2" data-testid="site-media">
            {d.sections.media.map((m, i) => {
              const embed = toEmbed(m.url);
              return (
                <div key={i} className="rounded-xl border p-3" style={{ background: t.surface, borderColor: "#0001" }}>
                  {m.label && <div className="mb-2 flex items-center gap-1.5 text-sm font-medium">{m.type === "audio" ? <MusicNotes size={16} style={{ color: t.accent }} /> : <FilmSlate size={16} style={{ color: t.accent }} />} {m.label}</div>}
                  {embed ? (
                    <div className="aspect-video w-full overflow-hidden rounded-lg"><iframe src={embed} title={m.label || `media-${i}`} className="h-full w-full" allowFullScreen frameBorder="0" /></div>
                  ) : m.type === "audio" ? (
                    <audio controls src={m.url} className="w-full" />
                  ) : (
                    <a href={m.url} target="_blank" rel="noreferrer" className="text-sm hover:underline" style={{ color: t.primary }}>{m.url}</a>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {d.sections?.links?.length > 0 && (
        <Section id="links" title="Links & Resources" t={t}>
          <div className="grid gap-2 sm:grid-cols-2" data-testid="site-links">
            {d.sections.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border p-3 text-sm transition-shadow hover:shadow-md" style={{ background: t.surface, borderColor: "#0001" }}>
                <LinkSimple size={16} style={{ color: t.accent }} /> <span className="truncate">{l.label || l.url}</span>
              </a>
            ))}
          </div>
        </Section>
      )}

      {d.notes && (
        <Section id="notes" title="Notes" t={t}>
          <div className="flex gap-3 rounded-2xl border p-5" style={{ background: t.surface, borderColor: "#0001" }} data-testid="site-notes">
            <NotePencil size={18} className="mt-0.5 shrink-0" style={{ color: t.accent }} />
            <p className="whitespace-pre-line text-base leading-relaxed opacity-90">{d.notes}</p>
          </div>
        </Section>
      )}

      {has("events") && d.events?.length > 0 && (
        <Section id="events" title={T.events || "Events & Reunions"} t={t}>
          <div className="space-y-3">
            {d.events.map((e) => (
              <Link key={e.id} to={`/events/${e.id}`} className="flex items-center gap-4 rounded-xl border p-4 transition-opacity hover:opacity-80" style={{ background: t.surface, borderColor: "#0001" }}>
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg text-white" style={{ background: t.accent }}><CalendarBlank size={20} /></div>
                <div><div className="font-medium">{e.title}</div><div className="text-xs opacity-60">{e.date ? new Date(e.date).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" }) : ""}{e.venue || e.location ? ` · ${e.venue || e.location}` : ""}</div></div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {has("alumni") && (
        <Section id="alumni" title={T.alumni || "Notable Alumni"} t={t}>
          {d.alumni?.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {d.alumni.map((a, i) => (
                <Link key={i} to={a.public_slug ? `/u/${a.public_slug}` : "#"} className="text-center">
                  {a.avatar_file_id ? <img src={fileUrl(a.avatar_file_id)} alt={a.name} className="mx-auto h-16 w-16 rounded-full object-cover" /> : <div className="mx-auto grid h-16 w-16 place-items-center rounded-full text-white" style={{ background: t.primary }}>{(a.name || "?")[0]}</div>}
                  <div className="mt-2 text-sm font-medium">{a.name}</div>
                  <div className="text-xs opacity-60">{a.headline}</div>
                </Link>
              ))}
            </div>
          ) : <p className="opacity-70">Be the first alumnus to join — {d.alumni_count ?? 0} registered so far.</p>}
        </Section>
      )}

      {has("contact") && (
        <Section id="contact" title={T.contact || "Contact"} t={t}>
          <div className="rounded-2xl border p-6" style={{ background: t.surface, borderColor: "#0001" }}>
            <div className="grid gap-3 sm:grid-cols-2">
              {d.location && <div className="flex items-center gap-2"><MapPin size={16} style={{ color: t.accent }} /> {d.location}{d.country ? `, ${d.country}` : ""}</div>}
              {d.website && <a href={d.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline"><Globe size={16} style={{ color: t.accent }} /> {d.website}</a>}
            </div>
          </div>
        </Section>
      )}

      <Section id="discussion" title="Discussion" t={t}>
        <p className="mb-5 -mt-3 text-sm opacity-70">Reconnect with fellow alumni — share memories and leave a comment.</p>
        <SiteDiscussion instId={d.id} t={t} />
      </Section>

      <div className="px-6 pt-8"><MemoryLanePromo label={d.name} /></div>

      <footer className="mt-6 border-t px-6 py-8 text-center text-sm opacity-60" style={{ borderColor: "#0001" }}>
        <p className="flex items-center justify-center gap-1.5">Powered by <Link to="/" className="inline-flex items-center gap-1 font-medium" style={{ color: t.primary }}><Sparkle size={13} weight="fill" /> AlumniPulse</Link></p>
      </footer>
    </div>
  );
}
